import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from '../components/icons';
import { Badge, Button, EmptyState, Field, Input, Select, Spinner } from '../components/ui';
import { Notice, Page, PageHeader, Section, Stack } from '../components/Page';
import type { ApiKeyCreatedDto } from '../lib/types';

/**
 * Server-to-server credentials.
 *
 * The whole screen is shaped by one fact: the key exists in exactly one HTTP response and
 * then never again. So the value is shown in a panel that does not go away on its own, the
 * copy button is the most prominent thing on it, and dismissing it says plainly that the
 * key cannot be recovered. Nothing here is allowed to navigate away while it is on screen.
 */
export function ApiKeys() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<ApiKeyCreatedDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const keys = useQuery({ queryKey: ['api-keys'], queryFn: () => api.apiKeys.list() });

  const revoke = useMutation({
    mutationFn: (keyId: string) => api.apiKeys.revoke(keyId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['api-keys'] }),
    onError: (e: Error) => setError(e.message),
  });

  if (keys.isLoading) return <div className="p-8"><Spinner /></div>;

  const data = keys.data?.data ?? [];

  return (
    <Page>
      <PageHeader
        icon="ticket"
        title="API keys"
        description="For servers and scripts calling FormForge on your behalf. A key carries a
                     subset of your own permissions and can be revoked at any time."
        actions={
          !creating && !issued && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} /> New key
            </Button>
          )
        }
      />

      <Stack>
        {error && <Notice tone="danger">{error}</Notice>}

        {issued && <IssuedKey issued={issued} onDismiss={() => setIssued(null)} />}

        {creating && (
          <CreateKey
            onCancel={() => setCreating(false)}
            onCreated={(key) => {
              setCreating(false);
              setIssued(key);
              void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
            }}
            onError={setError}
          />
        )}

        {data.length === 0 && !creating && (
          <EmptyState
            title="No API keys"
            description="Nothing is calling the API as this workspace yet."
            action={<Button variant="primary" onClick={() => setCreating(true)}>Create a key</Button>}
          />
        )}

        {data.length > 0 && (
          <Section title="Keys">
            <ul className="space-y-2">
              {data.map((key) => (
                <li
                  key={key.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border
                             border-[var(--ff-border)] p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{key.name}</span>
                      {key.status === 'active' && <Badge tone="success">active</Badge>}
                      {key.status === 'revoked' && <Badge tone="danger">revoked</Badge>}
                      {key.status === 'expired' && <Badge tone="warning">expired</Badge>}
                      {key.environment === 'test' && <Badge>test</Badge>}
                    </div>

                    <div className="mt-1 font-mono text-xs text-[var(--ff-text-muted)]">
                      {key.keyPrefix}…
                    </div>

                    <div className="mt-1 text-xs text-[var(--ff-text-muted)]">
                      {key.scopes.join(', ') || 'no scopes'}
                      {key.allowedIps.length > 0 && ` · from ${key.allowedIps.join(', ')}`}
                    </div>

                    <div className="mt-1 text-xs text-[var(--ff-text-muted)]">
                      created {new Date(key.createdUtc).toLocaleDateString()}
                      {/* Written as "never used" rather than left blank: a key nobody has
                          called is the safest one to revoke, and worth being able to spot. */}
                      {key.lastUsedUtc
                        ? ` · last used ${new Date(key.lastUsedUtc).toLocaleString()}`
                        : ' · never used'}
                      {key.expiresUtc && ` · expires ${new Date(key.expiresUtc).toLocaleDateString()}`}
                    </div>
                  </div>

                  {key.status === 'active' && (
                    <Button
                      variant="danger"
                      onClick={() => revoke.mutate(key.id)}
                      disabled={revoke.isPending}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </Stack>
    </Page>
  );
}

function IssuedKey({
  issued, onDismiss,
}: { issued: ApiKeyCreatedDto; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
    } catch {
      // Clipboard access is refused in some browsers and over plain HTTP. The value is
      // selectable on screen either way, so this is a convenience and never the only route.
      setCopied(false);
    }
  };

  return (
    <Section title={`Key created: ${issued.name}`}>
      <Notice tone="warning">{issued.warning}</Notice>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code
          className="min-w-0 flex-1 overflow-x-auto rounded-md border
                     border-[var(--ff-border-strong)] bg-[var(--ff-surface-sunken)]
                     px-3 py-2 font-mono text-sm"
        >
          {issued.secret}
        </code>

        <Button variant="primary" onClick={() => void copy()}>
          <Icon name={copied ? 'check' : 'copy'} size={14} />
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <div className="mt-3">
        <Button onClick={onDismiss}>I have stored it — hide this</Button>
      </div>
    </Section>
  );
}

function CreateKey({
  onCancel, onCreated, onError,
}: {
  onCancel: () => void;
  onCreated: (key: ApiKeyCreatedDto) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState('live');
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresUtc, setExpiresUtc] = useState('');
  const [allowedIps, setAllowedIps] = useState('');

  // The server decides which scopes exist and which this account can pass on, so the list
  // is fetched rather than hard-coded — a copy here would drift and start offering scopes
  // the server refuses.
  const available = useQuery({
    queryKey: ['api-keys', 'scopes'],
    queryFn: () => api.apiKeys.scopes(),
  });

  const create = useMutation({
    mutationFn: () => api.apiKeys.create({
      name,
      scopes,
      environment,
      expiresUtc: expiresUtc ? new Date(expiresUtc).toISOString() : null,
      allowedIps: allowedIps
        .split(/[\s,]+/)
        .map((v) => v.trim())
        .filter(Boolean),
    }),
    onSuccess: onCreated,
    onError: (e: Error) => onError(e.message),
  });

  const toggle = (scope: string) => setScopes((current) =>
    current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]);

  return (
    <Section title="New API key">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" hint="What is calling. You will read this when deciding whether to revoke it.">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nightly export job"
          />
        </Field>

        <Field label="Environment" hint="Only a label — both work identically against this server.">
          <Select value={environment} onChange={(e) => setEnvironment(e.target.value)}>
            <option value="live">Live</option>
            <option value="test">Test</option>
          </Select>
        </Field>

        <Field label="Expires" hint="Optional. A key with an end date is one you cannot forget about.">
          <Input type="date" value={expiresUtc} onChange={(e) => setExpiresUtc(e.target.value)} />
        </Field>

        <Field
          label="Allowed addresses"
          hint="Optional. Addresses or CIDR ranges, comma separated. Empty means anywhere."
        >
          <Input
            value={allowedIps}
            onChange={(e) => setAllowedIps(e.target.value)}
            placeholder="203.0.113.4, 10.0.0.0/8"
          />
        </Field>
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-semibold">Scopes</div>

        <div className="grid gap-1.5 sm:grid-cols-2">
          {(available.data?.data ?? []).map((entry) => (
            <label
              key={entry.scope}
              className={`flex items-center gap-2 rounded-md border p-2 text-sm ${
                entry.available
                  ? 'border-[var(--ff-border)]'
                  : 'border-[var(--ff-border-subtle)] opacity-50'
              }`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-[var(--ff-primary)]"
                checked={scopes.includes(entry.scope)}
                // A key can never exceed its creator, so a scope this account does not
                // hold is shown greyed rather than hidden: knowing why it is unavailable
                // is more use than wondering where it went.
                disabled={!entry.available}
                onChange={() => toggle(entry.scope)}
              />
              <span className="font-mono text-xs">{entry.scope}</span>
              {!entry.available && (
                <span className="ml-auto text-xs text-[var(--ff-text-muted)]">
                  you do not have this
                </span>
              )}
            </label>
          ))}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <Button
          variant="primary"
          onClick={() => create.mutate()}
          disabled={create.isPending || name.trim().length === 0 || scopes.length === 0}
        >
          {create.isPending ? 'Creating…' : 'Create key'}
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>

      {scopes.length === 0 && (
        <p className="mt-2 text-xs text-[var(--ff-text-muted)]">
          Choose at least one scope — a key with none can do nothing.
        </p>
      )}
    </Section>
  );
}
